import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRODUCTS, BUD, TURF_TALK } from "../../data/content";
import { Reveal } from "../Reveal";

const bySlug = (slug) => PRODUCTS.find((p) => p.slug === slug);

// Compact multi-path commercial entry immediately after the institution-first
// hero. Represents the whole institution — no single product family is the
// entirety of what THG sells or operates. Maturity labels stay distinct.
const LANES = [
    {
        title: "Commercial Software",
        text: "Operating systems and decision tools built through WarRoom Solutions.",
        items: ["warroom-storecentral", "control-shift", "warroom-ai"].map((slug) => {
            const p = bySlug(slug);
            const cues = {
                "warroom-storecentral": "Enterprise / multi-location pricing",
                "control-shift": "Launch & volume pricing available",
                "warroom-ai": "Founding launch offer — $149/month",
            };
            return {
                name: p.slug === "warroom-ai" ? "Sarge / WarRoom AI" : p.name,
                status: p.status,
                to: `/portfolio/${p.slug}`,
                cue: cues[p.slug],
            };
        }),
    },
    {
        title: "Operating Products & Media",
        text: "Consumer-facing, media, and demonstrable products that THG owns, operates, or develops with affiliated entities.",
        items: [
            { name: BUD.name, status: BUD.status, to: "/portfolio/bud" },
            { name: TURF_TALK.name, status: "Closed Beta · Controlled Demonstrations", to: "/portfolio/turf-talk-2" },
            { name: "HotelsTrips.com", status: bySlug("hoteltrips").status, to: "/portfolio/hoteltrips" },
        ],
    },
    {
        title: "Operational & Institutional Services",
        text: "Operational intelligence, retail performance work, government and research support, governance modernization, and strategic communications.",
        items: [
            { name: "WarRoom", status: "Operational Intelligence", to: "/warroom" },
            { name: "Government & Research", status: "Institutional Services", to: "/government-research" },
            { name: "Service Inquiries", status: "Route to the right desk", to: "/contact" },
        ],
    },
];

export const CommercialRoute = () => (
    <section className="border-b border-thg-border bg-[#0d0d0d]" data-testid="commercial-route">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <Reveal>
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-thg-red">Current Commercial Paths</p>
                <p className="mt-4 font-display text-lg md:text-xl leading-relaxed text-white max-w-3xl">
                    Titan Haven Group builds and operates commercial software, digital products and media
                    properties, and provides operational and institutional services.
                </p>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl" data-testid="commercial-why">
                    Across every lane, the reason is the same: Titan Haven Group looks for the gap between what
                    systems report, what conventional answers assume, and what is actually happening — then builds
                    or operates what the problem requires.
                </p>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-thg-secondary max-w-3xl" data-testid="venture-studio-note">
                    Selected products are developed through THG&rsquo;s operator-led venture studio model, with
                    commercialization paths that may include continued operation, licensing, strategic partnership, or
                    acquisition.
                </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-thg-border border border-thg-border">
                {LANES.map((lane, li) => (
                    <Reveal key={lane.title} delay={li * 0.07} className="bg-[#0a0a0a] p-7 md:p-8">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-thg-secondary">{lane.title}</p>
                        <p className="mt-3 text-sm leading-relaxed text-thg-tertiary">{lane.text}</p>
                        <div className="mt-6 space-y-3">
                            {lane.items.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.to}
                                    data-testid={`commercial-lane-${li}-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className="group block text-sm text-white hover:text-thg-secondary transition-colors duration-300"
                                >
                                    <span className="flex items-baseline justify-between gap-3">
                                        <span className="inline-flex items-center gap-1.5">
                                            {item.name}
                                            <ArrowRight
                                                size={13}
                                                className="text-thg-tertiary transition-transform duration-300 group-hover:translate-x-1"
                                            />
                                        </span>
                                        <span className="text-[10px] tracking-[0.12em] uppercase text-thg-tertiary text-right shrink-0">
                                            {item.status}
                                        </span>
                                    </span>
                                    {item.cue && (
                                        <span className="block mt-0.5 text-xs text-thg-red/90">{item.cue}</span>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
